import { create } from 'zustand';
import { createJSONStorage, persist } from 'zustand/middleware';
import qs from 'qs';
import {
  Event,
  EventCategory,
  EventCategoriesResponse,
  EventMutationResult,
  EventsResponse,
  EventType,
  RecurringEventMutationContext,
  getEventCategorySlugs,
  isRecurringEventType,
} from '@/types';
import { getEventFilterRange, normalizeExternalUrl, parseDateOnly } from '@/utils';
import { getUploadErrorMessage, uploadFilesSequentially } from '@/lib/upload';
import { fetchStrapiCollection } from '@/lib/strapi';

interface CreateEventData {
  title: string;
  description?: string;
  date: string;
  endDate?: string | null;
  timeStart?: string;
  timeEnd?: string;
  location: string;
  cost: string;
  eventType?: EventType;
  bookingLink?: string;
  groupDocumentId?: string;
  categorySlugs?: string[];
  organizerName?: string;
  openToPublic?: boolean;
  image?: File | null;
  images?: File[];
}

interface UpdateEventData {
  title?: string;
  description?: string;
  date?: string;
  endDate?: string | null;
  timeStart?: string;
  timeEnd?: string;
  location?: string;
  cost?: string;
  bookingLink?: string;
  openToPublic?: boolean;
  eventType?: EventType;
  categorySlugs?: string[] | null;
  image?: File | null;
  images?: File[];
  // Multi-image edit flow (mirrors the Groups edit pattern): the existing media
  // the admin kept, plus any newly picked files. When either is provided the
  // event's full `images` set is replaced with `[...existingImageIds, ...uploads]`
  // and the single `image` thumbnail is synced to the first of that set.
  existingImageIds?: number[];
  newImages?: File[];
}

interface EventState {
  events: Event[];
  upcomingEvents: Event[];
  categories: EventCategory[];
  loading: boolean;
  error: string | null;
  selectedCategories: string[];
  selectedMonth: string;
  searchQuery: string;
  monthRangeEnd: string | null;
  hasMoreEvents: boolean;
  eventsPage: number;
  fetchEvents: (opts?: { page?: number }) => Promise<void>;
  loadMoreEvents: () => Promise<void>;
  fetchUpcomingEvents: () => Promise<void>;
  fetchCategories: () => Promise<void>;
  setSelectedCategories: (slugs: string[]) => void;
  setSelectedMonth: (month: string) => void;
  setSearchQuery: (query: string) => void;
  resetFilters: () => void;
  toggleAttend: (documentId: string) => Promise<void>;
  hasUserAttending: (documentId: string, userId: number) => boolean;
  createEvent: (data: CreateEventData) => Promise<EventMutationResult>;
  updateEvent: (
    documentId: string,
    data: UpdateEventData,
    recurringContext?: RecurringEventMutationContext,
  ) => Promise<EventMutationResult>;
  deleteEvent: (
    documentId: string,
    recurringContext?: RecurringEventMutationContext,
  ) => Promise<EventMutationResult>;
  getSortedEvents: (
    userGroupIds?: number[],
    viewer?: EventViewerContext,
  ) => Event[];
}

/**
 * Identifies who is viewing the event list so private-event visibility can
 * extend beyond group members: a site admin sees every private event, and an
 * event's author always sees the events they created (MWBI-592).
 */
export interface EventViewerContext {
  isSiteAdmin?: boolean;
  currentUserId?: number;
}

const baseUrl = '/api/strapi';
const DEFAULT_SELECTED_MONTH = 'all';
const DEFAULT_SELECTED_CATEGORIES: string[] = [];

function uploadEventImages(files: File[]): Promise<number[]> {
  return uploadFilesSequentially(files, 'Failed to upload event images');
}

function formatDateForQuery(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');

  return `${year}-${month}-${day}`;
}

function isLaterDate(candidate: string, currentMax: string | null): boolean {
  return currentMax === null || candidate > currentMax;
}

export function getMonthOptions(
  language: 'en' | 'es' = 'en',
  t?: { events: { allDates: string; thisMonth: string } },
  _monthRangeEnd?: string | null,
): { label: string; value: string }[] {
  const locale = language === 'es' ? 'es-ES' : 'en-US';
  const allDatesLabel = t?.events?.allDates || 'All Dates';
  const thisMonthLabel = t?.events?.thisMonth || 'This Month';
  const now = new Date();
  const months = [
    { label: allDatesLabel, value: 'all' },
    { label: thisMonthLabel, value: 'this-month' },
  ];

  // Show 12 months forward from current month (may span year boundary).
  for (let i = 1; i <= 12; i += 1) {
    const date = new Date(now.getFullYear(), now.getMonth() + i, 1);
    const label = date.toLocaleDateString(locale, { month: 'long', year: 'numeric' });
    const value = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
    months.push({ label, value });
  }

  return months;
}

export const useEventStore = create<EventState>()(
  persist(
    (set, get) => ({
  events: [],
  upcomingEvents: [],
  categories: [],
  loading: false,
  error: null,
  selectedCategories: DEFAULT_SELECTED_CATEGORIES,
  selectedMonth: DEFAULT_SELECTED_MONTH,
  searchQuery: '',
  monthRangeEnd: null,
  hasMoreEvents: false,
  eventsPage: 1,

  fetchCategories: async () => {
    try {
      // MWBI-903 follow-up — event-category rows have no curated `order`
      // (schema default only, never intentionally set by seed data or any
      // admin UI). Some pre-existing rows have `order: null` while newer
      // ones default to `order: 0`; sorting by `order:asc` first would split
      // the list into two separately-alphabetized runs (SQLite sorts NULL
      // before 0), so sort by name alone.
      const categories = await fetchStrapiCollection<EventCategoriesResponse['data'][number]>('event-categories', {
        sort: ['name:asc'],
      });
      set({ categories });
    } catch (error) {
      console.error('Error fetching event categories:', error);
    }
  },

  fetchEvents: async (opts?: { page?: number }) => {
    const page = opts?.page ?? 1;
    try {
      if (page === 1) set({ loading: true, error: null });

      const { selectedMonth, selectedCategories } = get();
      const { rangeStart, rangeEnd } = getEventFilterRange(selectedMonth);
      const rangeStartStr = formatDateForQuery(rangeStart);
      const rangeEndStr = rangeEnd ? formatDateForQuery(rangeEnd) : null;

      const filters: Record<string, unknown> = {
        deletedAt: { $null: true },
      };

      if (selectedCategories.length > 0) {
        filters.categories = { slug: { $in: selectedCategories } };
      }

      // Each occurrence is its own row with its own date, so a plain date
      // range catches every occurrence in the window — no recurrence math.
      filters.date = rangeEndStr
        ? { $gte: rangeStartStr, $lte: rangeEndStr }
        : { $gte: rangeStartStr };

      const query = qs.stringify(
        {
          filters,
          populate: {
            image: { fields: ['url', 'alternativeText'] },
            images: { fields: ['url', 'alternativeText'] },
            categories: { fields: ['id', 'documentId', 'name', 'slug'] },
            group: { fields: ['id', 'documentId', 'name', 'eventListVisible'] },
            attendees: { fields: ['id'] },
            author: { fields: ['id', 'displayName', 'username', 'initials', 'avatarUrl', 'avatarColor'] },
          },
          sort: ['date:asc'],
          pagination: { page, pageSize: 100 },
        },
        { encodeValuesOnly: true }
      );

      const url = `${baseUrl}/events?${query}`;
      const res = await fetch(url, { cache: 'no-store' });

      if (!res.ok) throw new Error('Failed to fetch events');

      const data: EventsResponse = await res.json();
      const hasMore = selectedMonth === 'all' && page < data.meta.pagination.pageCount;

      if (page === 1) {
        const nextMonthRangeEnd = data.data.reduce<string | null>((maxDate, event) => {
          return isLaterDate(event.date, maxDate) ? event.date : maxDate;
        }, get().monthRangeEnd);

        set({ events: data.data, loading: false, monthRangeEnd: nextMonthRangeEnd, hasMoreEvents: hasMore, eventsPage: 1 });
      } else {
        set({ events: [...get().events, ...data.data], loading: false, hasMoreEvents: hasMore, eventsPage: page });
      }
    } catch (error) {
      console.error('eventStore.fetchEvents failed', error);
      set({ error: (error as Error).message, loading: false });
    }
  },

  loadMoreEvents: async () => {
    await get().fetchEvents({ page: get().eventsPage + 1 });
  },

  fetchUpcomingEvents: async () => {
    try {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const todayStr = formatDateForQuery(today);

      const query = qs.stringify(
        {
          filters: {
            deletedAt: { $null: true },
            date: { $gte: todayStr },
            openToPublic: { $ne: false },
          },
          populate: {
            image: { fields: ['url', 'alternativeText'] },
            images: { fields: ['url', 'alternativeText'] },
            categories: { fields: ['id', 'documentId', 'name', 'slug'] },
            group: { fields: ['id', 'documentId', 'name', 'eventListVisible'] },
            attendees: { fields: ['id'] },
            author: { fields: ['id', 'displayName', 'username', 'initials', 'avatarUrl', 'avatarColor'] },
          },
          sort: ['date:asc'],
          pagination: { limit: 3 },
        },
        { encodeValuesOnly: true }
      );

      const url = `${baseUrl}/events?${query}`;
      const res = await fetch(url, { cache: 'no-store' });

      if (!res.ok) throw new Error('Failed to fetch upcoming events');

      const data: EventsResponse = await res.json();
      set({ upcomingEvents: data.data });
    } catch (error) {
      console.error('Error fetching upcoming events:', error);
    }
  },

  setSelectedCategories: (slugs: string[]) => {
    set({ selectedCategories: slugs });
    get().fetchEvents();
  },

  setSelectedMonth: (month: string) => {
    set({ selectedMonth: month });
    get().fetchEvents();
  },

  setSearchQuery: (query: string) => {
    set({ searchQuery: query });
  },

  resetFilters: () => {
    set({
      selectedCategories: DEFAULT_SELECTED_CATEGORIES,
      selectedMonth: DEFAULT_SELECTED_MONTH,
      searchQuery: '',
      hasMoreEvents: false,
      eventsPage: 1,
    });
    get().fetchEvents();
  },

  toggleAttend: async (documentId: string) => {
    try {
      const res = await fetch(`${baseUrl}/events/${documentId}/toggle-attend`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({}),
      });

      if (!res.ok) throw new Error('Failed to toggle attend');

      const { data } = await res.json();

      set((state) => ({
        events: state.events.map(e =>
          e.documentId === documentId
            ? {
                ...e,
                attendees: data.attendees ?? (data.attending ? [...(e.attendees || [])] : []),
                attendeeCount: data.attendeeCount,
              }
            : e
        ),
      }));
    } catch (err) {
      console.error('Error toggling attend:', err);
    }
  },

  hasUserAttending: (documentId: string, userId: number) => {
    const event = get().events.find(e => e.documentId === documentId);
    return event?.attendees?.some(a => a.id === userId) ?? false;
  },

  createEvent: async (data: CreateEventData) => {
    try {
      set({ loading: true, error: null });

      const selectedCategorySlugs = data.categorySlugs?.filter(Boolean) ?? [];
      if (selectedCategorySlugs.length > 0 && get().categories.length === 0) {
        await get().fetchCategories();
      }

      const categoryDocumentIds = selectedCategorySlugs
        .map((slug) => get().categories.find((category) => category.slug === slug)?.documentId)
        .filter((documentId): documentId is string => Boolean(documentId));

      // Upload images first if provided
      const filesToUpload = data.images?.length ? data.images : (data.image ? [data.image] : []);
      const imageIds = await uploadEventImages(filesToUpload);
      const imageId = imageIds[0] ?? null;

      // Build the event data object
      const eventData: Record<string, unknown> = {
        title: data.title,
        date: data.date,
        endDate: data.endDate ?? null,
        location: data.location,
        timeStart: data.timeStart || '',
        timeEnd: data.timeEnd || '',
        description: data.description || '',
        cost: data.cost,
        eventType: data.eventType || 'one-off',
        bookingLink: normalizeExternalUrl(data.bookingLink) || '',
        organizer: data.organizerName || '',
        openToPublic: data.openToPublic ?? true,
        attendeeCount: 0,
      };

      // Add image relation if uploaded
      if (imageId) {
        eventData.image = imageId;
      }
      if (imageIds.length > 0) {
        eventData.images = imageIds;
      }

      // Add group relation if provided (use documentId for Strapi 5)
      if (data.groupDocumentId) {
        eventData.group = { connect: [data.groupDocumentId] };
      }

      if (categoryDocumentIds.length > 0) {
        eventData.categories = { connect: categoryDocumentIds };
      }

      // Create the event. A recurring eventType is exploded into one row per
      // occurrence by the backend controller, all sharing a seriesId.
      const res = await fetch(`${baseUrl}/events`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ data: eventData }),
      });

      if (!res.ok) {
        throw new Error(await getUploadErrorMessage(res, 'Failed to create event'));
      }

      const { data: createdEvent } = await res.json() as { data: { documentId: string } };

      // Refresh events list
      await get().fetchEvents();
      set({ loading: false });
      return { success: true, eventDocumentId: createdEvent.documentId };
    } catch (error) {
      console.error('Error creating event:', error);
      const message = (error as Error).message;
      set({ error: message, loading: false });
      return { success: false, error: message };
    }
  },

  updateEvent: async (
    documentId: string,
    data: UpdateEventData,
    recurringContext?: RecurringEventMutationContext,
  ) => {
    try {
      const selectedCategorySlugs = data.categorySlugs?.filter(Boolean) ?? [];
      if (data.categorySlugs !== undefined && get().categories.length === 0) {
        await get().fetchCategories();
      }

      const categoryDocumentIds = data.categorySlugs === undefined
        ? undefined
        : selectedCategorySlugs
          .map((slug) => get().categories.find((category) => category.slug === slug)?.documentId)
          .filter((documentId): documentId is string => Boolean(documentId));

      // Multi-image edit flow (existingImageIds + newImages), mirroring the
      // Groups edit pattern. Keep the existing media the admin didn't remove and
      // append any newly uploaded files, then replace the whole `images` set and
      // sync the single `image` thumbnail to the first of that set. Resolved
      // before the payload branch so it flows through BOTH the one-off PUT and
      // the recurring update-occurrences path (that action forwards image/images
      // — it only strips per-occurrence and server-managed fields).
      const isManagingImageSet =
        data.existingImageIds !== undefined || data.newImages !== undefined;
      let resolvedImageIds: number[] | undefined;
      if (isManagingImageSet) {
        const existingIds = data.existingImageIds ?? [];
        const newlyUploadedIds = data.newImages && data.newImages.length > 0
          ? await uploadEventImages(data.newImages)
          : [];
        resolvedImageIds = [...existingIds, ...newlyUploadedIds];
      }

      // Legacy direct-file path (callers that pass File(s) without managing the
      // existing set). Skipped when the multi-image edit flow is in use.
      let imageId: number | undefined;
      let imageIds: number[] | undefined;
      if (!isManagingImageSet) {
        if (data.images && data.images.length > 0) {
          imageIds = await uploadEventImages(data.images);
          imageId = imageIds[0];
        } else if (data.image) {
          const uploadedFiles = await uploadEventImages([data.image]);
          imageId = uploadedFiles[0];
        }
      }

      // Build update payload (exclude image files / control fields from JSON)
      const updatePayload: Record<string, unknown> = { ...data };
      delete updatePayload.categorySlugs;
      delete updatePayload.image;
      delete updatePayload.images;
      delete updatePayload.existingImageIds;
      delete updatePayload.newImages;

      if (data.endDate !== undefined) {
        updatePayload.endDate = data.endDate;
      }

      if (data.bookingLink !== undefined) {
        updatePayload.bookingLink = normalizeExternalUrl(data.bookingLink) || '';
      }

      if (resolvedImageIds !== undefined) {
        // Replace the gallery with exactly the resolved set ([] clears it) and
        // keep the single `image` thumbnail in sync with the first entry.
        updatePayload.images = resolvedImageIds;
        updatePayload.image = resolvedImageIds[0] ?? null;
      } else {
        if (imageId) {
          updatePayload.image = imageId;
        }
        if (imageIds && imageIds.length > 0) {
          updatePayload.images = imageIds;
        }
      }

      if (categoryDocumentIds !== undefined) {
        updatePayload.categories = { set: categoryDocumentIds };
      }

      // Recurring edit: the backend updateOccurrences action applies the change
      // to this occurrence (this-event) or this and every later one
      // (this-and-future) and strips per-occurrence/managed fields itself.
      if (recurringContext) {
        const res = await fetch(`${baseUrl}/events/${documentId}/update-occurrences`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            data: { ...updatePayload, scope: recurringContext.scope },
          }),
        });

        if (!res.ok) {
          const errorBody = await res.text();
          console.error('Failed to update recurring event response:', errorBody);
          throw new Error('Failed to update recurring event');
        }

        await get().fetchEvents();
        return { success: true, eventDocumentId: documentId };
      }

      // A recurring eventType arriving WITHOUT a scope means "materialise this
      // standalone event into a real series" (MWBI-955). The two cases cannot
      // be confused: an edit to an existing series occurrence always arrives
      // with a scope (the detail page routes it there whenever seriesId is set),
      // and updateOccurrences strips eventType regardless. Before this branch
      // existed the cadence rode the plain PUT below and silently did nothing.
      if (isRecurringEventType(data.eventType)) {
        const res = await fetch(`${baseUrl}/events/${documentId}/convert-to-series`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ data: updatePayload }),
        });

        if (!res.ok) {
          const errorBody = await res.text();
          console.error('Failed to convert event to a series response:', errorBody);
          throw new Error('Failed to convert event to a recurring series');
        }

        await get().fetchEvents();
        return { success: true, eventDocumentId: documentId };
      }

      const res = await fetch(`${baseUrl}/events/${documentId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ data: updatePayload }),
      });

      if (!res.ok) {
        const errorBody = await res.text();
        console.error('Failed to update event payload:', updatePayload);
        console.error('Failed to update event response:', errorBody);
        throw new Error('Failed to update event');
      }

      await get().fetchEvents();
      return { success: true, eventDocumentId: documentId };
    } catch (error) {
      console.error('Error updating event:', error);
      return { success: false, error: (error as Error).message };
    }
  },

  deleteEvent: async (
    documentId: string,
    recurringContext?: RecurringEventMutationContext,
  ) => {
    try {
      // Soft delete via the custom endpoint. A recurring scope tells the
      // backend to also soft-delete every later occurrence in the series.
      const res = await fetch(`${baseUrl}/events/${documentId}/delete`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(
          recurringContext ? { data: { scope: recurringContext.scope } } : {},
        ),
      });

      if (!res.ok) {
        throw new Error('Failed to delete event');
      }

      await get().fetchEvents();
      return { success: true, eventDocumentId: documentId };
    } catch (error) {
      console.error('Error deleting event:', error);
      return { success: false };
    }
  },

  getSortedEvents: (userGroupIds: number[] = [], viewer: EventViewerContext = {}) => {
    const { events, selectedCategories } = get();
    const visibleEvents: Event[] = [];

    events.forEach(event => {
      if (
        selectedCategories.length > 0
        && !getEventCategorySlugs(event).some((slug) => selectedCategories.includes(slug))
      ) {
        return;
      }

      // Client-side mirror of cms/server/src/utils/visibility-filters.ts
      // buildEventVisibilityFilter. The server already excludes these rows;
      // this is the same defence-in-depth the openToPublic check has always
      // been. Keep the two in step — if the server rule changes, change this.
      if (event.group?.id) {
        const isMember = userGroupIds.includes(event.group.id);
        const isAuthor = viewer.currentUserId != null
          && event.author?.id === viewer.currentUserId;
        const isInsider = isMember || viewer.isSiteAdmin || isAuthor;

        if (!isInsider) {
          // MWBI-890 — the group gate is the master control: while a group
          // hides its events, a non-member sees none of them, INCLUDING ones
          // marked openToPublic. Only an explicit `false` hides; undefined /
          // null reads as showing, matching the server's $null branch so
          // legacy rows stay visible.
          if (event.group.eventListVisible === false) {
            return; // Skip this event
          }
          // Then the event's own Access setting (MWBI-592 predates this).
          if (event.openToPublic === false) {
            return; // Skip this event
          }
        }
      }
      visibleEvents.push(event);
    });

    // Sort by date (upcoming first)
    const sortByDate = (a: Event, b: Event) =>
      parseDateOnly(a.date).getTime() - parseDateOnly(b.date).getTime();

    return visibleEvents.sort(sortByDate);
  },
    }),
    {
      name: 'event-filters',
      storage: createJSONStorage(() => sessionStorage),
      partialize: (state) => ({
        selectedMonth: state.selectedMonth,
        selectedCategories: state.selectedCategories,
        searchQuery: state.searchQuery,
      }),
    },
  ),
);
